// One "view" of the notifications list — a tab plus the Unread-only switch —
// shared by the bell panel and the /notifications page so they can't drift.
//
// Mount it only where the tabs are actually showing (inside the open bell
// panel, or the page). That is what scopes the per-type count query to "while
// someone is looking", and it gives the bell its reset-on-close for free: the
// panel unmounts when it closes, taking the pins below with it.

import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useMarkAllNotificationsRead,
  useNotifications,
  useSetNotificationRead,
  useUnreadCount,
  useUnreadTypeCounts,
  type Notification,
} from '@/lib/queries';
import { typesForTab, unreadByTab, type NotificationTabId } from '@/lib/notification-categories';

interface PinState {
  viewKey: string;
  rows: Map<string, Notification>;
}

// Never mutated — `pinRow` always copies before writing.
const NO_PINS = new Map<string, Notification>();

interface Args {
  limit: number;
  tab: NotificationTabId;
  unreadOnly: boolean;
  /** Called after a row is activated (e.g. the bell closes its panel). */
  onNavigate?: () => void;
}

export function useNotificationsView({ limit, tab, unreadOnly, onNavigate }: Args) {
  const qc = useQueryClient();
  const listQ = useNotifications(limit, { tab, unreadOnly });
  const typeCountsQ = useUnreadTypeCounts();
  const totalQ = useUnreadCount();
  const setRead = useSetNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  // ---- Pinned rows ----
  //
  // In an Unread-only view the server stops returning a row the moment it is
  // read. If it then vanished, a mis-click could not be undone and the rows
  // below would jump up under the cursor mid-triage. So a row the user
  // individually acts on is pinned in place — shown as read, toggle still
  // live — until the view changes (tab switch, switch off, panel closed, page
  // left). The counts still update immediately; only the row's presence lingers.
  const viewKey = `${tab}|${unreadOnly ? 'unread' : 'all'}`;
  const [pins, setPins] = useState<PinState>({ viewKey, rows: NO_PINS });
  // Reset when the view changes. Done during render (React's "adjust state
  // when a prop changes" pattern) rather than in an effect, so a stale pin can
  // never flash for a frame — or resurface when the user returns to a view
  // whose key matches an old pin set.
  if (pins.viewKey !== viewKey) setPins({ viewKey, rows: NO_PINS });
  const activePins = pins.viewKey === viewKey ? pins.rows : NO_PINS;

  const pinRow = useCallback(
    (n: Notification) => {
      setPins((prev) => {
        const rows = prev.viewKey === viewKey ? new Map(prev.rows) : new Map<string, Notification>();
        rows.set(n.id, n);
        return { viewKey, rows };
      });
    },
    [viewKey],
  );

  const rows = useMemo(() => {
    const byId = new Map<string, Notification>();
    for (const n of listQ.data ?? []) byId.set(n.id, n);
    // Server copy wins; a pin only fills in a row the server no longer sends.
    for (const [id, n] of activePins) if (!byId.has(id)) byId.set(id, n);
    let merged = [...byId.values()];
    // In Unread-only, a read row that ISN'T pinned was marked read some other
    // way (Mark all read, opening its ticket, another device) — drop it now
    // instead of waiting for the refetch.
    if (unreadOnly) merged = merged.filter((n) => n.read_at === null || activePins.has(n.id));
    return merged.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  }, [listQ.data, activePins, unreadOnly]);

  // A tab with no cached rows shows placeholder data that is legitimately
  // empty (e.g. Resolved, filtered out of the warm All list) for the moment
  // before the real result lands. Treat that as loading, not as "nothing here".
  const showSkeleton = listQ.isLoading || (listQ.isPlaceholderData && rows.length === 0);

  const byTab = useMemo(
    () => unreadByTab(typeCountsQ.data, totalQ.data),
    [typeCountsQ.data, totalQ.data],
  );

  const toggleRead = useCallback(
    (n: Notification) => {
      const read = n.read_at === null; // unread → read, read → unread
      setRead.mutate({ id: n.id, type: n.type, read });
      if (unreadOnly) pinRow({ ...n, read_at: read ? new Date().toISOString() : null });
    },
    [setRead, unreadOnly, pinRow],
  );

  const activate = useCallback(
    (n: Notification) => {
      if (n.read_at === null) {
        setRead.mutate({ id: n.id, type: n.type, read: true });
        if (unreadOnly) pinRow({ ...n, read_at: new Date().toISOString() });
      }
      // Force-refetch the target ticket's data. Without this, clicking a
      // notification for the ticket the user is ALREADY viewing is a no-op
      // router transition — the URL doesn't change, the component doesn't
      // unmount, and React Query's cached data stays put, so the new client
      // reply that triggered the notification never appears until a manual
      // refresh. Invalidating the three queries the ticket detail page reads
      // (header, messages, events) covers every panel that needs the update.
      if (n.ticket_id) {
        qc.invalidateQueries({ queryKey: ['ticket', n.ticket_id] });
        qc.invalidateQueries({ queryKey: ['ticket-messages', n.ticket_id] });
        qc.invalidateQueries({ queryKey: ['ticket-events', n.ticket_id] });
      }
      onNavigate?.();
    },
    [qc, setRead, unreadOnly, pinRow, onNavigate],
  );

  const markAllRead = useCallback(() => {
    markAll.mutate({ types: typesForTab(tab) });
    // "Mark all read" is the one action that empties the view straight away.
    setPins({ viewKey, rows: NO_PINS });
  }, [markAll, tab, viewKey]);

  return {
    rows,
    showSkeleton,
    isError: listQ.isError,
    unreadByTab: byTab,
    activate,
    toggleRead,
    markAllRead,
    /** Whether the active tab has anything unread to mark. */
    canMarkAll: byTab[tab] > 0,
    markAllPending: markAll.isPending,
  };
}
